/**
 * CustomTable Component
 *
 * A highly customizable, feature-rich table component with built-in support for sorting,
 * searching, pagination, export, bulk actions, and contextual row-level menus or actions.
 * Designed for scalable data presentation and interaction in admin dashboards and data-driven UIs.
 *
 *
 * MAJOR FEATURES:
 * 1. Data Display:
 *    - Responsive and styled Bootstrap-based table layout
 *    - Sticky header support for long tables
 *    - Row click handler for external interaction
 *    - Dynamic rendering of table cells via `columns.render`
 *
 * 2. Search and Sorting:
 *    - Built-in search with debounce (custom or internal)
 *    - Column-level sorting using lodash's `orderBy`
 *    - Case-insensitive string comparison for sorting
 *
 * 3. Pagination:
 *    - Integrated pagination with customizable page size
 *    - Remembers user's selection using cookies (`recordsPerPage`)
 *    - Server-side and client-side pagination supported via props
 *
 * 4. Export and Bulk Actions:
 *    - CSV export with proper value escaping via `csvValidator`
 *    - Optional toolbar bulk action button based on row selection
 *    - Flexible action dropdowns per row
 *
 * 5. Inline Actions:
 *    - Built-in support for Edit, Delete, and More actions
 *    - Additional custom actions can be passed using `additionalActions`
 *    - Conditional rendering logic for action visibility
 *
 * UI/UX IMPROVEMENTS:
 * 1. Layout:
 *    - Toolbar with optional filters, search, and add button
 *    - Download and bulk action icons integrated in top-right corner
 *    - Consistent spacing and interaction feedback
 *
 * 2. Interactivity:
 *    - Clickable rows for detail view
 *    - Sort indicators using up/down arrows
 *    - Adaptive header controls per column
 *
 * 3. Responsiveness:
 *    - Table layout adapts to screen sizes
 *    - Select dropdown for page limit is mobile-friendly
 *    - Overflow handling and auto-scroll in long tables
 *
 * TECHNICAL ENHANCEMENTS:
 * 1. Performance:
 *    - Debounced search using lodash
 *    - Optimized sorting and filtering via memoized callbacks
 *    - Dynamic data slicing for pagination performance
 *
 * 2. Extensibility:
 *    - Column config via `columns` array: { field, title, class, render, data-cy }
 *    - Extensible toolbar and row-level action slots
 *    - Optional server-side pagination and search integration
 *
 * 3. Utility Integrations:
 *    - CSV export using `react-csv`
 *    - Cookie handling with `js-cookie` for persistent page size
 *    - Controlled pagination and dropdown via external components
 *
 * @component
 * @param {Object} props
 * @param {Array} props.columns - Array of column configurations (field, title, class, render)
 * @param {Array} props.data - Array of row data to display
 * @param {boolean} props.isAction - Enables contextual action column
 * @param {Array} props.additionalActions - Custom per-row actions with `icon`, `label`, `handler`, `show`
 * @param {Array} props.toolbarActions - Additional actions in the table toolbar
 * @param {Function} props.menu - Overlay menu renderer (for MUI/Bootstrap dropdown)
 * @param {Function} props.handleMenu - Callback for when row menu icon is clicked
 * @param {boolean|Function} props.isMore - Flag or function to enable row "more" icon
 * @param {JSX.Element} props.isMoreIcon - Custom icon for the "more" button
 * @param {Function} props.isMoreHandler - Handler for clicking the "more" icon
 * @param {boolean} props.isEdit - Enables Edit icon per row
 * @param {Function} props.editHandler - Edit click handler
 * @param {boolean|Function} props.isDelete - Enables Delete icon per row
 * @param {Function} props.deleteHandler - Delete click handler
 * @param {boolean} props.exportButton - Show/hide export CSV button
 * @param {boolean} props.addButton - Show/hide Add button
 * @param {Function} props.addHandler - Handler for Add button
 * @param {boolean} props.toolbar - Show/hide the entire toolbar
 * @param {string} props.fileName - Filename for CSV export
 * @param {boolean} props.enableSearch - Enables search input
 * @param {number} props.limit - Pagination limit
 * @param {number} props.itemsPerPage - Default items per page
 * @param {number} props.currentPage - Initial page
 * @param {Object} props.categoryFilterProps - Props for the optional CategoryFilter dropdown
 * @param {boolean} props.itemsPerPageCustom - Enables items-per-page selector
 * @param {Array} props.selectedItem - Optional list of selected rows
 * @param {Function} props.selectAllAction - Optional bulk action handler
 * @param {Function} props.selectAllBtnHandler - Optional bulk action trigger
 * @param {boolean} props.selectAll - Select all toggle
 * @param {number} props.paginationCount - Total item count for pagination (useful for server-side)
 * @param {boolean} props.stickyHeader - Enables sticky table headers
 * @param {Function} props.rowClickHandler - Callback when a row is clicked
 * @param {Function} props.searchHandler - Optional external search handler (server-side search)
 * @param {Function} props.pageHandler - Optional external pagination handler (server-side paging)
 * @param {Function} props.limitChangeHandler - Optional limit change callback
 * @returns {JSX.Element} Fully interactive, paginated, sortable table component
 */

import React, { useCallback, useEffect, useState, useRef } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { CSVLink } from "react-csv";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PaginationComponent from "./pagination";
import CategoryFilter from "./category-filter";
import SelectInput from "../../components/inputs/select";
import CustomInput from "../inputs/new-input";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerInput from "../inputs/date-picker";
import _ from "lodash";
import { csvValidator } from "../../util/validators/csv";
import { debounceTime } from "../../util/common";
import { setCookie } from "../../util/auth";
import { PerPageCount } from "src/util/enums";
import Cookies from "js-cookie";
import { format } from "date-fns";
import { pattern } from "../../util/validators/pattern";
import { z } from "zod";
import {
  CircularProgress,
  InputAdornment,
  TextField,
  IconButton,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";

const CustomTable = ({
  columns = [],
  data = [],
  isAction = false,
  additionalActions = [],
  toolbarActions = [],
  menu,
  handleMenu,
  isMore,
  isMoreIcon = <MoreHorizIcon />,
  isMoreHandler,
  isEdit,
  editHandler,
  isDelete,
  deleteHandler,
  exportButton = true,
  addButton = false,
  addHandler,
  toolbar = true,
  fileName = "data",
  enableSearch = true,
  limit = PerPageCount.DEFAULT,
  itemsPerPage = PerPageCount.DEFAULT,
  currentPage = 1,
  categoryFilterProps,
  itemsPerPageCustom = true,
  selectedItem,
  selectAllAction,
  selectAllBtnHandler,
  selectAll,
  paginationCount = data.length,
  stickyHeader = false,
  rowClickHandler,
  emptyMessage = "No Data",
  searchHandler,
  pageHandler,
  limitChangeHandler,
  dataCy = "table",
  isLoading,
  externalSearchValue,
  cookieName = "recordsPerPage",
  sortBy,
  sortHandler,
  ...props
}) => {
  const [search, setSearch] = useState(externalSearchValue ?? "");
  const [columnSearches, setColumnSearches] = useState({});
  const [page, setPage] = useState(currentPage);
  const [rows, setRows] = useState(data);
  const [sort, setSort] = useState(sortBy ?? "");
  const [itemTotalCount, setItemTotalCount] = useState(data.length);
  const [searchError, setSearchError] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const itemsPerPageList = React.useMemo(() => {
    return [
      { key: `${PerPageCount.DEFAULT} per page`, value: PerPageCount.DEFAULT },
      { key: `${PerPageCount.CUSTOM} per page`, value: PerPageCount.CUSTOM },
      { key: `${PerPageCount.MAX} per page`, value: PerPageCount.MAX }
    ];
  }, []);

  const cookieVal = itemsPerPageCustom && Cookies.get(cookieName) ? parseInt(Cookies.get(cookieName)) : null;
  const recordsPerPage = cookieVal && itemsPerPageList.some(item => item.value === cookieVal)
    ? cookieVal
    : itemsPerPage;

  const [activeColumnSearches, setActiveColumnSearches] = useState([]);
  const searchInputRefs = useRef({});
  const isDragging = useRef(false);
  const clickTimeoutRef = useRef(null);
  const limitChangedRef = useRef(false);
  const clickCountRef = useRef(0);

  const searchSchema = z.string().trim();

  // Effect: Syncs internal search state with external prop changes.
  //whenever you switch tabs, the table will use the same sharedSearch text as the previous one.
  useEffect(() => {
    if (externalSearchValue !== search) {
      setSearch(externalSearchValue || "");
    }
  }, [externalSearchValue]);

  // Effect: Auto-apply an externally provided search value (e.g. from a URL query).
  useEffect(() => {
    if (data.length > 0 && externalSearchValue !== undefined) {
      filterRows(externalSearchValue, data, columnSearches);
    }
  }, [externalSearchValue, data, columnSearches]);

  useEffect(() => {
    if (sortBy !== sort) {
      setSort(sortBy || "");
    }
  }, [sortBy]);

  // Effect: Syncs table rows and total count with incoming data.

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    setRows(data);
    setItemTotalCount(data.length);
  }, [data]);

  const [fields, setFields] = useState({
    itemsPerPage: {
      name: "itemsPerPage",
      type: "select",
      touched: false,
      error: false,
      valid: true,
      value: recordsPerPage,
      items: [],
    },
  });

  // Effect: Updates pagination options - always show all options regardless of data count.
  useEffect(() => {
    setFields((prevState) => ({
      ...prevState,
      itemsPerPage: {
        ...prevState.itemsPerPage,
        items: itemsPerPageList,
      },
    }));
  }, [itemsPerPageList]);

  // Effect: Applies sorting when sort state changes.
  useEffect(() => {
    if (sort) {
      if (sortHandler) {
        sortHandler(sort);
      } else {
        sortRows();
      }
    }
  }, [sort]);

  // Effect: Updates rows with selection state when selected items change.
  useEffect(() => {
    if (selectedItem !== null && selectedItem !== undefined) {
      const updatedRows = data.map((item) => {
        const selectedTemplate = selectedItem.find((sel) => sel.id === item.id);
        if (selectedTemplate) {
          return {
            ...item,
            isSelected: true,
            selectedQty: selectedTemplate.selectedQty,
          };
        }
        return { ...item, isSelected: false, selectedQty: "" };
      });
      setRows(updatedRows);
      setItemTotalCount(updatedRows.length);
    }
  }, [data, selectedItem]);

  const prevColumnSearchesRef = useRef({});


  // Debounces column-specific search input to filter rows.
  const debouncedColumnSearch = useCallback(
    _.debounce((columnField, searchVal, currentData) => {
      const trimmedSearchVal = searchVal.trimStart();
      setColumnSearches((prev) => {
        const newColumnSearches = { ...prev, [columnField]: trimmedSearchVal };
        if (
          JSON.stringify(newColumnSearches) !==
          JSON.stringify(prevColumnSearchesRef.current)
        ) {
          prevColumnSearchesRef.current = newColumnSearches;
          filterRows(search, currentData, newColumnSearches);
        }
        return newColumnSearches;
      });
    }, debounceTime.columnSearch),
    [search, data]
  );

  // Effect: Triggers global search when search input changes.
  // useEffect(() => {
  //   debouncedSearch(search, data);
  //   return () => {
  //     debouncedSearch.cancel();
  //   };
  // }, [search, data, debouncedSearch]);

  //Effect: triggers global search when an specifically assigned icon is clicked
  const handleSearch = async () => {
    const result = searchSchema.safeParse(search);

    if (!result.success) {
      setSearchError(result.error.errors[0].message);
      return;
    }

    setSearchError(null);

    const searchVal = result.data.trimStart();

    try {
      if (searchHandler) {
        setPage?.(1);
        await searchHandler(searchVal);
      } else {
        filterRows?.(searchVal, data, columnSearches);
      }
    } finally {
    }
  };

  // Detects Enter key press in the search input to trigger search.
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Effect: Triggers pagination updates for server-side paging.
  // Skip when a limit change already fired limitChangeHandler (avoids duplicate API calls).
  useEffect(() => {
    if (limitChangedRef.current) {
      limitChangedRef.current = false;
      return;
    }
    if (pageHandler) {
      pageHandler(page, fields.itemsPerPage.value);
    }
  }, [page, fields.itemsPerPage.value]);

  // Description: Sorts table rows based on the selected column and sort direction.
  const sortRows = () => {
    if (sort) {
      let isDesc = sort.startsWith("-");
      let key = sort.replace("-", "");
      const sortedArray = _.orderBy(
        rows,
        [
          (user) =>
            typeof user[key] === "string" ? user[key].toLowerCase() : user[key],
        ],
        [isDesc ? "desc" : "asc"]
      );
      setRows(sortedArray || []);
      setItemTotalCount(sortedArray.length);
    }
  };

  // A helper function to safely get the value to be searched from an item.
  const getSearchableValue = (item, column) => {
    // If the column has a custom render function and it's not the 'selectall' or a simple value,
    // we use a simplified version of the render output for searching.
    if (column.render && column.field !== "selectall") {
      const renderedValue = column.render(item);
      // We need to handle JSX elements and extract their text content.
      if (typeof renderedValue === "object" && renderedValue !== null) {
        // This is a common pattern for extracting text from React elements.
        return renderedValue?.props?.children?.toString() || "";
      }
      return renderedValue?.toString() || "";
    }
    // For regular fields, directly return the item's value.
    return item[column.field]?.toString() || "";
  };

  const filterRows = (
    globalSearchVal,
    dataToSearch = data,
    columnSearches = {}
  ) => {
    const escapeRegex = (str) => str.replace(pattern.specialChars, "\\$&"); // Escapes special characters in the search string
    let _data = _.cloneDeep(dataToSearch);

    // Apply global search
    if (globalSearchVal && globalSearchVal.length > 1) {
      _data = _data.filter((item) =>
        columns.some((column) => {
          const searchableValue = getSearchableValue(item, column);
          return (
            searchableValue.search(
              new RegExp(escapeRegex(globalSearchVal), "gi")
            ) > -1
          );
        })
      );
    }

    // Apply column-specific searches
    Object.keys(columnSearches).forEach((field) => {
      const searchVal = columnSearches[field];
      if (searchVal && searchVal.length > 0) {
        _data = _data.filter((item) => {
          const column = columns.find((c) => c.field === field);
          if (!column) return false;
          const searchableValue = getSearchableValue(item, column);
          return (
            searchableValue.search(new RegExp(escapeRegex(searchVal), "gi")) >
            -1
          );
        });
      }
    });

    setRows(_data);
    setItemTotalCount(_data.length);
    setPage(1);
  };

  // Updates pagination limit and adjusts page number, saving to cookie.
  const handleLimitChange = (newLimit) => {
    if (isLoading) return;
    setCookie({ name: cookieName, value: newLimit });
    // Mark that this page update is caused by a limit change so
    // the pageHandler useEffect does NOT fire a duplicate API call.
    limitChangedRef.current = true;
    setPage(1);
    setFields((prevState) => ({
      ...prevState,
      itemsPerPage: {
        ...prevState.itemsPerPage,
        value: newLimit,
      },
    }));
    if (limitChangeHandler) {
      limitChangeHandler(newLimit);
    }
  };

  // Updates the current page for pagination.
  const handlePageChange = (newPage) => {
    if (isLoading) return;
    const validPage = newPage > 0 ? newPage : 1;
    setPage(validPage);
  };

  // Triggers column-specific search with debouncing.
  const handleColumnSearch = (field, value) => {
    debouncedColumnSearch(field, value, data);
  };

  // Checks if a column is a date type for rendering date picker.
  const isDateColumn = (field) => {
    const col = columns.find((c) => c.field === field);
    return col && col.type === "date";
  };

  // Activates column-specific search input and focuses it.
  const handleSearchIconClick = (event, field) => {
    if (isLoading) return;
    event.stopPropagation();
    setActiveColumnSearches((prevActive) =>
      prevActive.includes(field) ? prevActive : [...prevActive, field]
    );
    setTimeout(() => {
      if (searchInputRefs.current[field]) {
        searchInputRefs.current[field].focus();
      }
    }, 0);
  };

  // Clears a column's search filter and refreshes table data.
  const handleClearColumnSearch = (field) => {
    if (isLoading) return;
    setColumnSearches((prevSearch) => {
      const newSearch = { ...prevSearch };
      delete newSearch[field];
      filterRows(search, data, newSearch); // Re-filter rows after clearing
      return newSearch;
    });
    setActiveColumnSearches((prevActive) =>
      prevActive.filter((f) => f !== field)
    );
  };

  // Resets drag flag on mouse down for row interaction.
  const handleMouseDown = () => {
    isDragging.current = false;
  };

  // Detects drag events by checking text selection.
  const handleMouseMove = () => {
    if (window.getSelection().toString().length > 0) {
      isDragging.current = true;
    }
  };

  // Handles row clicks, distinguishing between single and double clicks.
  const handleRowInteraction = (item, event) => {
    // Prevent right-click (button 2) from triggering row click
    if (event && event.button === 2) {
      return;
    }

    if (isDragging.current) {
      isDragging.current = false;
      return;
    }

    clickCountRef.current++;

    if (clickCountRef.current === 1) {
      clickTimeoutRef.current = setTimeout(() => {
        if (rowClickHandler) {
          rowClickHandler(item); // Execute single click handler
        }
        clickCountRef.current = 0; // Reset click count
      }, 250);
    } else if (clickCountRef.current === 2) {
      // Second click within the timeout: This is a double click
      clearTimeout(clickTimeoutRef.current); // Prevent single click from firing
      clickCountRef.current = 0; // Reset click count
    }
  };

  const tableClassName = `table-create table-material table-material-ch ${stickyHeader ? "sticky-header" : ""
    }`;

  return (
    <div className="row category-row" data-cy={dataCy}>
      <div className="col-12">
        {toolbar && (
          <div className="row m-0">
            {categoryFilterProps && (
              <div className="col-2 col-lg-3 col-md-3 col-xxl-3 ms-auto category-list-dropdown">
                <CategoryFilter {...categoryFilterProps} />
              </div>
            )}
            <div className="d-flex justify-content-end align-items-center ms-2 gap-4 search-container">
              {toolbarActions.map((action, index) => (
                <span key={index}>{action}</span>
              ))}
              {enableSearch && (
                <div
                  style={{ width: "100%", maxWidth: 400, position: "relative" }}
                >
                  <TextField
                    disabled={isLoading}
                    fullWidth
                    variant="outlined"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleKeyDown}
                    error={!!searchError}
                    helperText={searchError}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    aria-label="Global search"
                    data-cy="table-search"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <div key={isLoading ? "loading" : "idle"}>
                            {isLoading ? (
                              <CircularProgress
                                size={20}
                                style={{ color: "#555555", marginLeft: 4 }}
                              />
                            ) : (
                              <IconButton
                                onClick={handleSearch}
                                edge="start"
                                disabled={isLoading}
                                style={{
                                  padding: 8,
                                  color: isFocused ? "#1976d2" : "#666666", // text-primary vs muted
                                  transition: "color 0.2s ease-in-out",
                                }}
                              >
                                <SearchIcon />
                              </IconButton>
                            )}
                          </div>
                        </InputAdornment>
                      ),
                    }}
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "4px", // rounded-xl
                      transition: "all 0.2s ease-in-out",
                      boxShadow: isFocused
                        ? "0 4px 12px rgba(25, 118, 210, 0.35)" // shadow-primary/10
                        : "0 2px 6px rgba(0,0,0,0.05)",
                    }}
                    InputLabelProps={{
                      style: { color: "#9e9e9e" },
                    }}
                    inputProps={{
                      style: {
                        fontSize: 16,
                        color: "#333",
                        padding: "10px 12px",
                      },
                    }}
                  />
                </div>
              )}

              {exportButton && (
                <span className="download-icon">
                  <CSVLink
                    data={data.map((item) => {
                      const modifiedItem = {};
                      for (const key in item) {
                        const value = item[key];
                        if (typeof value === "string" && csvValidator(value)) {
                          modifiedItem[key] = `'${value}`;
                        } else {
                          modifiedItem[key] = value;
                        }
                      }
                      return columns.map((column) => {
                        let f_value = column.render
                          ? column.render(modifiedItem)
                          : modifiedItem[column.field];
                        return typeof f_value === "string"
                          ? f_value
                          : modifiedItem[column.field];
                      });
                    })}
                    headers={columns.map((column) => column.title)}
                    filename={`${fileName}.csv`}
                  >
                    <FileDownloadOutlinedIcon />
                  </CSVLink>
                </span>
              )}
              {addButton && addHandler && (
                <span onClick={addHandler} className="ms-2 download-icon">
                  <AddIcon />
                </span>
              )}
              {rows.some((row) => row.isSelected && row.status === "PENDING") &&
                selectAllAction &&
                selectAllBtnHandler && (
                  <button
                    onClick={() => selectAllBtnHandler(rows)}
                    className="col-3 secondarybtn secondarybtn-outline btn-ch w-auto w-res-100 mx-3 me-0 btn btn-primary"
                  >
                    Bulk Action
                  </button>
                )}
            </div>
          </div>
        )}
        <div className={`material-list-block ${toolbar && "mt-3"}`}>
          <div className="table-responsive">
            <Table bordered className={tableClassName} data-cy={dataCy}>
              <thead>
                <tr>
                  {columns.map((column, index) => (
                    <th
                      key={index}
                      className={column.class}
                      data-cy={column["data-cy"] || column?.dataCy || ""}
                      style={{ position: "relative" }}
                    >
                      <div className="align-items-center justify-content-between">
                        {activeColumnSearches.includes(column.field) ? (
                          <div
                            className={`${column.class} d-flex align-items-center column-search-active`}
                            style={{ position: "relative" }}
                          >
                            {isDateColumn(column.field) ? (
                              <div className="date-picker">
                                <DatePickerInput
                                  value={columnSearches[column.field] || ""}
                                  onChange={(val) =>
                                    handleColumnSearch(
                                      column.field,
                                      val
                                        ? format(new Date(val), "dd/MM/yyyy")
                                        : ""
                                    )
                                  }
                                  placeholder={column.title}
                                  clearable={true}
                                  className="column-date-picker"
                                  popperProps={{
                                    anchorOrigin: {
                                      vertical: "bottom",
                                      horizontal: "left",
                                    },
                                    transformOrigin: {
                                      vertical: "top",
                                      horizontal: "left",
                                    },
                                    sx: { zIndex: 9999 },
                                  }}
                                />
                              </div>
                            ) : (
                              <div className="search-bar">
                                <CustomInput
                                  ref={(el) =>
                                    (searchInputRefs.current[column.field] = el)
                                  }
                                  type="text"
                                  placeholder={`${column.title}`}
                                  value={columnSearches[column.field] || ""}
                                  customInputClass="fst-12 p-2 min-w-150"
                                  onChange={(e) =>
                                    handleColumnSearch(
                                      column.field,
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            )}
                            <CloseIcon
                              className="close-icon"
                              style={{ cursor: "pointer", marginLeft: "8px" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClearColumnSearch(column.field);
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className={`${column.class} ${column.columnSearch
                              ? "column-search-inactive d-flex flex-row align-items-center"
                              : ""
                              }`}
                          >
                            <span
                              onClick={() => {
                                if (isLoading) return;
                                if (sort.includes(column.field)) {
                                  setSort(
                                    sort.startsWith("-")
                                      ? column.field
                                      : `-${column.field}`
                                  );
                                } else {
                                  setSort(column.field);
                                }
                              }}
                              style={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              {column.render && column.field === "selectall"
                                ? column.render({ isHeader: true, rows: data })
                                : column.title}
                              {sort.includes(column.field) && (
                                <>
                                  {sort.startsWith("-") ? (
                                    <ArrowDownwardIcon fontSize="small" />
                                  ) : (
                                    <ArrowUpwardIcon fontSize="small" />
                                  )}
                                </>
                              )}
                            </span>
                            {column.columnSearch && (
                              <SearchIcon
                                className="search-icon"
                                onClick={(event) =>
                                  handleSearchIconClick(event, column.field)
                                }
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                  {isAction && <th>Action</th>}
                  {(isEdit ||
                    isDelete ||
                    isMore ||
                    additionalActions?.length > 0) && <th></th>}
                </tr>
              </thead>
              <tbody>
                {(pageHandler
                  ? _.slice(rows, 0, fields.itemsPerPage.value)
                  : _.slice(
                    rows,
                    fields.itemsPerPage.value * (page - 1),
                    fields.itemsPerPage.value * page
                  )
                ).map((item, itemIndex) => (
                  <tr
                    key={itemIndex}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={(event) => handleRowInteraction(item, event)}
                  >
                    {columns.map((column, index) => (
                      <td
                        key={index}
                        className={column.class}
                        data-cy={column["data-cy"] || column?.dataCy || ""}
                      >
                        {column.render
                          ? column.render(item)
                          : item[column.field]}
                      </td>
                    ))}
                    {isAction && (
                      <td>
                        <div className="action-dropdown">
                          <OverlayTrigger
                            trigger="click"
                            rootClose
                            placement="bottom"
                            overlay={menu ? menu(item) : <></>}
                          >
                            <Button
                              variant="default"
                              data-cy="action-menu"
                              onClick={() => {
                                if (handleMenu) handleMenu(item);
                              }}
                            >
                              <MoreHorizIcon />
                            </Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    )}
                    {(isEdit ||
                      isDelete ||
                      isMore ||
                      additionalActions?.length > 0) && (
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            {isMore &&
                              ((typeof isMore === "function" && isMore(item)) ||
                                typeof isMore === "boolean") && (
                                <div className="action-edit">
                                  <span
                                    onClick={() => {
                                      if (isMoreHandler) isMoreHandler(item);
                                    }}
                                    data-cy="action-more"
                                  >
                                    {isMoreIcon}
                                  </span>
                                </div>
                              )}
                            {isEdit && (
                              <div className="action-edit">
                                <EditIcon
                                  onClick={() => {
                                    if (editHandler) editHandler(item);
                                  }}
                                  data-cy="action-edit"
                                />
                              </div>
                            )}
                            {isDelete &&
                              ((typeof isDelete === "function" &&
                                isDelete(item)) ||
                                typeof isDelete === "boolean") && (
                                <div className="action-edit">
                                  <DeleteIcon
                                    className="deleteicon"
                                    onClick={() => {
                                      if (deleteHandler) deleteHandler(item);
                                    }}
                                    data-cy="action-delete"
                                  />
                                </div>
                              )}
                            {additionalActions?.map(
                              (action, index) =>
                                ((typeof action.show === "function" &&
                                  action.show(item)) ||
                                  action.show === true) && (
                                  <div key={index} className="action-edit">
                                    <span
                                      onClick={() => action.handler(item)}
                                      title={action.label}
                                      data-cy="action-custom"
                                    >
                                      {action.icon}
                                    </span>
                                  </div>
                                )
                            )}
                          </div>
                        </td>
                      )}
                  </tr>
                ))}
                {(isLoading && rows.length === 0) ? (
                  // Skeleton placeholder rows
                  Array.from({ length: 5 }).map((_, rowIndex) => (
                    <tr key={`skeleton-row-${rowIndex}`}>
                      {Array.from({
                        length:
                          columns.length +
                          (isAction ? 1 : 0) +
                          (isEdit ||
                            isDelete ||
                            isMore ||
                            additionalActions?.length > 0
                            ? 1
                            : 0),
                      }).map((_, colIndex) => (
                        <td key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                          <Skeleton
                            variant="text"
                            animation="pulse"
                            width="80%"
                            height={20}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (!isLoading && rows.length < 1) ? (
                  <tr>
                    <td
                      colSpan={
                        columns.length +
                        (isAction ? 1 : 0) +
                        (isEdit ||
                          isDelete ||
                          isMore ||
                          additionalActions?.length > 0
                          ? 1
                          : 0)
                      }
                      className="text-center"
                    >
                      {emptyMessage || "No Data"}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
      {((searchHandler || pageHandler) ? paginationCount : itemTotalCount) > 0 && (
        <div className="col-12 pt-3 d-flex justify-content-center align-items-center gap-3 flex-wrap">
          <div className="pagination-component-wrapper">
            <PaginationComponent
              itemsCount={(searchHandler || pageHandler) ? paginationCount : itemTotalCount}
              itemsPerPage={fields.itemsPerPage.value}
              currentPage={page}
              setCurrentPage={handlePageChange}
              freezePagninationCount={true}
            />
          </div>
          {itemsPerPageCustom && (
            <div className="pagination-dropdown">
              <SelectInput
                {...fields.itemsPerPage}
                value={fields.itemsPerPage.value}
                onChange={(e) => handleLimitChange(e.target.value)}
                customClassName="dynamic-table-select"
                className="custom-select-dropdown"
                data-cy="items-per-page-select"
                disabled={isLoading}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomTable;
